import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { AddCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { CustomButton, HumbeeTabs } from '../../../v2/MUI_custom_component';
import BillingInfo from '../../Components/BillingInfo';
import ProductCard from '../../Inventory/components/ProductCard';
import IllustrationWrapper from '../../Illustrration/IllustrationWrapper';
import EmptyCartSvg from '../../Illustrration/components/EmptyCartSvg';
import { deliveryTypeDropDown, OperationType } from '../../../utils/settings';
import { removeProduct, updateProductQty, selectShipToParty, resetSelectedShipToParty, removeShipToParty } from './reducer/DpActions';
import { selectShipToPartyCarts, selectSelectedShipToPartyId, selectSelectedCategory, selectSelectedFormType, selectDpRouteSource } from './reducer/DpSelectors';
import { GetCategoryProductsAction, getOrderCostAction, ModifyOrderAction } from './API/Action';
import { DP_ROUTE_SOURCE } from './Enums';
import IconReusable from '../../../components/IconReusable';
import { toCapitalized } from '../../../v2/Utils/Capitalize';
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal';
import DisclaimerSvg from '../../Illustrration/components/DisclaimerSvg';
import { buildModifyOrderPayload, buildProductInfo, buildShipToPartyInfo } from './../ModifyOrder'

const OrderSummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
      (state) => state.entityReducer
  );
  const stpAction = location.state?.stpAction ;
  const stpShipToPartyId = location.state?.shipToPartyId;
  const selectedGrade = useSelector(selectSelectedCategory)
  const selectedFormType = useSelector(selectSelectedFormType)
  const { loginUser } = useSelector((state) => ({
    loginUser: state.loginReducer.loginUser,
  }));
  const { order_booking_id } = useSelector((state) => {
    return {
      order_booking_id: state.dpReducer?.order_booking_id,
    };
  });
  const updatedPricingGeo = location.state?.updatedPricingGeo || null;
  const updatePricingGeoId = updatedPricingGeo ? updatedPricingGeo.value : null
  const [ ShowSTPModal, setShowSTPModal ] = useState(false);
  const [loading, setLoading] = useState(false)
  const [pendingSTPAction, setPendingSTPAction] = useState(null);
  const [pendingSTPEntry, setPendingSTPEntry] = useState(null);
  const fetchProducts = async (updatePricingGeoId) => {
    if (!updatePricingGeoId || !selectedCompanyEntity) return
    const payload = {
      sku_value_id: [
        Number(selectedGrade),
        Number(selectedFormType),
      ],
    };
    const {data, error, message } = await GetCategoryProductsAction(selectedCompanyEntity, payload, updatePricingGeoId)
    if (!error) {
      updateCartItemsPrices(data)
    }else{
      toast.error(message)
    }
  }
  const updateCartItemsPrices = (newProductList) => {
    if (!newProductList?.length) return;

    Object.entries(shipToPartyCarts).forEach(([shipToPartyId, cartData]) => {
      if (!cartData?.cart?.length) return;

      cartData.cart.forEach(cartItem => {
        const updatedProduct = newProductList.find(
          p => p.product_id === cartItem.product_id
        );

        if (
          updatedProduct &&
          Number(updatedProduct.current_price_per_unit) !==
            Number(cartItem.current_price_per_unit)
        ) {
          dispatch({
            type: 'UPDATE_SHIP_TO_PARTY_PRODUCT_PRICE',
            data: {
              shipToPartyId,
              productId: cartItem.product_id,
              newPrice: Number(updatedProduct.current_price_per_unit)
            }
          });
        }
      });
    });
  };

  useEffect(() => {
    if (updatePricingGeoId && selectedCompanyEntity) {
      fetchProducts(updatePricingGeoId)
    }
  }, [updatePricingGeoId, selectedCompanyEntity])

  const shipToPartyCarts = useSelector(selectShipToPartyCarts);
  const selectedShipToPartyId = useSelector(selectSelectedShipToPartyId);
  const shipToPartyList = useMemo(() => {
    const list = Object.entries(shipToPartyCarts).map(
      ([shipToPartyId, data]) => ({
        shipToPartyId,
        cart: data.cart || [],
        totals: data.totals || {},
        shipToPartyDetails: data.shipToPartyDetails || {},
        isAddTab: false
      })
    );
    if (list.length < 3) {
      list.push({ isAddTab: true });
    }

    return list;
  }, [shipToPartyCarts]);

  const [currentTab, setCurrentTab] = useState(0);
  useEffect(() => {
    if (
      shipToPartyList.length > 0 &&
      !shipToPartyList[currentTab]?.isAddTab &&
      !selectedShipToPartyId
    ) {
      dispatch(selectShipToParty(shipToPartyList[0].shipToPartyId));
    }
  }, [shipToPartyList, selectedShipToPartyId, dispatch, currentTab]);
  const getDeliveryTypeLabel = (value) => {
    const type = deliveryTypeDropDown.find(t => t.value === value);
    return type ? type.label : '-';
  };
  const handleAddProducts = () => {
    navigate('/create-dp', {
      state: {
        from: DP_ROUTE_SOURCE.ORDER_SUMMARY,
        shipToPartyId: selectedShipToPartyId,
        order_booking_id: location.state?.order_booking_id,
      }
    });
  };
  const handleAddShipToParty = () => {
    dispatch(resetSelectedShipToParty());

    navigate('/create-dp', {
      state: {
        isNewShipToParty: true,
        order_booking_id: location.state?.order_booking_id,
      }
    });
  };
  const updateShipToParty = async ([, stpData]) => {
    const { shipToPartyDetails, cart } = stpData;

    if (!shipToPartyDetails?.humbee_so_id) {
      toast.error('Invalid Ship to Party');
      return;
    }
    const updatedProducts = cart.filter(
      (p) => !p?.humbee_so_product_id
    );
    if (!updatedProducts.length) {
      toast.info('No product changes found');
    }
    const shipment = {
      ship_to_party_info: {
        humbee_so_id: shipToPartyDetails.humbee_so_id,

        company_info: {
          id: shipToPartyDetails.company_id,
          esi_id: shipToPartyDetails.selected_esi_id,
          external_system_code:
            shipToPartyDetails.selected_esi_info?.external_system_code,
          name: shipToPartyDetails.company_name,
          phone: shipToPartyDetails.company_phone_no,
          gst: shipToPartyDetails.gstin,
        },

        address_info: {
          address: {
            address_line_1: shipToPartyDetails.delivery_Address_line1,
            address_line_2: shipToPartyDetails.delivery_Address_line2,
            address_line_3: shipToPartyDetails.delivery_Address_line3,
          },
          pincode: shipToPartyDetails.delivery_company_pincode,
          district_info: {
            id: shipToPartyDetails.delivery_district_id,
            name: shipToPartyDetails.delivery_district_name,
          },
          state_info: {
            id: shipToPartyDetails.delivery_state_id,
            name: shipToPartyDetails.delivery_state_name,
          },
          location_info: {
            location_id: shipToPartyDetails.delivery_location_id,
            location_name: shipToPartyDetails.delivery_location_name,
          },
        },

        receiver_info: {
          name: shipToPartyDetails.receiver_name ?? '',
          phone_number: shipToPartyDetails.receiver_mobile ?? '',
        },

        delivery_info: {
          delivery_type: shipToPartyDetails.delivery_type,
          incoterms: shipToPartyDetails.incoterms ?? 2,
        },

        pricing_geography_info: {
          pricing_geography_id: shipToPartyDetails.pricing_geography_id,
          pricing_geography_name: shipToPartyDetails.pricing_geography_name,
        },
      },
    };

    const payload = buildModifyOrderPayload({
      orderId: order_booking_id,
      shipment,
      shipToPartyOperation: OperationType.UPDATE,
      products: updatedProducts.map((product) =>
        buildProductInfo(product, null)
      ),
    });

    setLoading(true);
    const { error, message } = await ModifyOrderAction(
      payload,
      selectedCompanyEntity
    );
    setLoading(false);

    if (!error) {
      toast.success(message);
      navigate(`/dispatch-plan-detail/${order_booking_id}`);
    } else {
      toast.error(message);
    }
  };
  const handleConfirmAndProceed = () => {
   
    if (stpAction === OperationType.ADD) {
      setPendingSTPAction(OperationType.ADD);
      setPendingSTPEntry(null);
      setShowSTPModal(true);
      return;
    }else if (stpAction === OperationType.UPDATE && stpShipToPartyId) {
      // const entry = Object.entries(shipToPartyCarts).find(
      //   ([id]) => id === stpShipToPartyId
      // );
      const entry = Object.entries(shipToPartyCarts).find(
        ([id]) => String(id) === String(stpShipToPartyId)
      );
      console.log('asdad')
      if (entry) {
        setPendingSTPAction(OperationType.UPDATE);
        setPendingSTPEntry(entry);
        setShowSTPModal(true);
        
        return;
      }
    }else{
      selectedManufacturer && getOrderSummary();
    }
  };

  const safeCapitalize = (text) => {
    return text && typeof text === 'string' ? toCapitalized(text) : text;
  };
  const getOrderSummary = async () => {
    const orderProductsDelivery = Object.entries(shipToPartyCarts).map(
      ([shipToPartyId, stpData]) => {
        const {
          cart,
          shipToPartyDetails
        } = stpData;
        return {
          ship_to_party_info: {
            so_id: 0,
            company_erp_code: shipToPartyDetails?.selected_esi_info.external_system_code,
            deliver_to: shipToPartyDetails.company_id || shipToPartyId,
            deliver_to_esi_id: shipToPartyDetails?.selected_esi_info.esi_id,
            company_name: shipToPartyDetails.company_name || '',
            company_gstin: shipToPartyDetails.gstin || '',
            company_pan: shipToPartyDetails.pancard_no || '',
            company_phone_no: shipToPartyDetails.company_phone_no || '',
            company_registered_address: [
              safeCapitalize(shipToPartyDetails?.company_address1),
              safeCapitalize(shipToPartyDetails?.company_address2),
              safeCapitalize(shipToPartyDetails?.company_address3),
              safeCapitalize(shipToPartyDetails?.location_name),
              safeCapitalize(shipToPartyDetails?.district_name),
              safeCapitalize(shipToPartyDetails?.state_name),
              safeCapitalize(shipToPartyDetails?.pincode),
            ]
              .filter(Boolean)
              .join(', ')
            ,
            delivery_address: {
              address_line_1: shipToPartyDetails.delivery_Address_line1 || '',
              address_line_2: shipToPartyDetails.delivery_Address_line2 || '',
              address_line_3: shipToPartyDetails.delivery_Address_line3 || '',
            },
            delivery_pincode: shipToPartyDetails.delivery_company_pincode,
            delivery_district_id: shipToPartyDetails.delivery_district_id || null,
            delivery_district_name: shipToPartyDetails.delivery_district_name || null,
            delivery_state_id: shipToPartyDetails.delivery_state_id || null,
            delivery_state_name: shipToPartyDetails.delivery_state_name || null,
            location_id: shipToPartyDetails.delivery_location_id || null,
            location_name: shipToPartyDetails.delivery_location_name || '',
            pricing_geography_id: updatedPricingGeo ? updatePricingGeoId : shipToPartyDetails.pricing_geography_id || null,
            pricing_geography_name: updatedPricingGeo ? updatedPricingGeo.label :  shipToPartyDetails.pricing_geography_name || null,
            delivery_type: shipToPartyDetails.delivery_type || null,
            delivery_type_name: getDeliveryTypeLabel(shipToPartyDetails.delivery_type),
            order_delivery_contact_no: shipToPartyDetails.receiver_mobile || '',
            order_delivery_contact_person_name: shipToPartyDetails.receiver_name || '',
          },

          products_info: cart.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            product_logo: item.product_image,
            price_info: {
              landing_price: item.current_price_per_unit,
            },
            quantity: {
              quantity_in_bags: item.quantity.quantity_in_bags,
              quantity_in_metric_ton: item.quantity.quantity_in_metric_ton,
              multiplier: item.quantity.multiplier,
              unit: item.quantity.unit,
              base_unit: item.quantity.base_unit,
            },
            ordered_quantity: null,
            dispatched_quantity: null,
            remaining_quantity: null,
            
          }))
        };
      }
    );
    const payload = {
      order_products_delivery: orderProductsDelivery,
      supplier_id: selectedManufacturer,
      buyer_id: loginUser.user_id,
      buyer_esi_id: selectedCompanyEntity,
      seller_id: selectedManufacturer,
      order_booking_id
    };
    setLoading(true);
    const { data, error, message } = await getOrderCostAction(payload);
    setLoading(false);
    if (!error) {
      navigate('/dp-checkout', { state: { checkOutData: data } });
    } else {
      toast.error(message);
    }
  };
  const renderCartItems = (cartItems, shipToPartyId) => {
    if (!cartItems.length) {
      return (
        <IllustrationWrapper
          illustration={<EmptyCartSvg />}
          title="Cart Empty!"
          subtitle="Add products to cart to proceed"
        />
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {cartItems.map(item => (
          <ProductCard
            key={item.product_id}
            productName={item.product_name}
            imageUrl={item.product_image}
            price={item.current_price_per_unit}
            quantity={item.quantity.quantity_in_metric_ton}
            showQuantityControls
            showDeleteButton
            onIncreasePress={() =>
              dispatch(
                updateProductQty(
                  item.product_id,
                  item.quantity.quantity_in_metric_ton + 1,
                  shipToPartyId
                )
              )
            }
            onReducePress={() =>
              dispatch(
                updateProductQty(
                  item.product_id,
                  item.quantity.quantity_in_metric_ton - 1,
                  shipToPartyId
                )
              )
            }
            onDelete={() =>
              dispatch(removeProduct(item.product_id, shipToPartyId))
            }
          />
        ))}
      </Box>
    );
  };

  const handleRemoveShipToParty = (shipToPartyId) => {
    // Find the index of the current tab being deleted
    const deletingTabIndex = shipToPartyList.findIndex(item => item.shipToPartyId === shipToPartyId);
    
    dispatch(removeShipToParty(shipToPartyId));
    
    // If deleting the 2nd or 3rd tab, select the first tab
    if (deletingTabIndex > 0) {
      setCurrentTab(0);
      const firstShipToParty = shipToPartyList[0];
      if (firstShipToParty && !firstShipToParty.isAddTab) {
        dispatch(selectShipToParty(firstShipToParty.shipToPartyId));
      }
    }
  }
  
  const AddShiptoParty = async () => {
    setShowSTPModal(false);
    const newShipToPartyEntry = Object.entries(shipToPartyCarts).find(
      ([, data]) => !data?.shipToPartyDetails?.humbee_so_id
    );
    if (!newShipToPartyEntry) {
      toast.error('No new Ship to Party found');
      return;
    }
    const [, stpData] = newShipToPartyEntry;
    const { cart, shipToPartyDetails } = stpData;

    if (!cart?.length) {
      toast.error('Add at least one product');
      return;
    }
    const shipment = {
      ship_to_party_info: {
        humbee_so_id : null,
        company_info: {
          id: shipToPartyDetails?.company_id,
          esi_id: shipToPartyDetails?.selected_esi_id,
          external_system_code: shipToPartyDetails?.selected_esi_info?.external_system_code,
          name: shipToPartyDetails?.company_name,
          phone: shipToPartyDetails?.company_phone_no,
          gst: shipToPartyDetails?.gstin,
        },
        address_info: {
          address: {
            address_line_1: shipToPartyDetails?.delivery_Address_line1,
            address_line_2: shipToPartyDetails?.delivery_Address_line2,
            address_line_3: shipToPartyDetails?.delivery_Address_line3,
          },
          pincode: shipToPartyDetails?.delivery_company_pincode,
          district_info: {
            id: shipToPartyDetails?.delivery_district_id,
            name: shipToPartyDetails?.delivery_district_name,
          },
          state_info: {
            id: shipToPartyDetails?.delivery_state_id,
            name: shipToPartyDetails?.delivery_state_name,
          },
          location_info: {
            location_id: shipToPartyDetails?.delivery_location_id,
            location_name: shipToPartyDetails?.delivery_location_name,
          },
        },
        receiver_info: {
          name: shipToPartyDetails?.receiver_name ?? '',
          phone_number: shipToPartyDetails?.receiver_mobile,
        },
        delivery_info: {
          delivery_type: shipToPartyDetails?.delivery_type,
          incoterms: shipToPartyDetails?.incoterms ?? 2,
        },
        pricing_geography_info: {
          pricing_geography_id: shipToPartyDetails?.pricing_geography_id,
          pricing_geography_name: shipToPartyDetails?.pricing_geography_name,
        },
      },
    };
    const payload = buildModifyOrderPayload({
      orderId: order_booking_id,
      shipment,
      shipToPartyOperation: OperationType.ADD,
      products: cart.map((product) =>
        buildProductInfo(product, OperationType.ADD)
      ),
    });
    setLoading(true)
    const { error, message } = await ModifyOrderAction(
      payload,
      selectedCompanyEntity
    );
    setLoading(false)

    if (!error) {
      toast.success(message);
      navigate(`/dispatch-plan-detail/${order_booking_id}`)
    } else {
      toast.error(message);
    }
  };

  useEffect(() => {
    const isShipToPartyEmpty =
      !shipToPartyCarts || Object.keys(shipToPartyCarts).length === 0;

    if (isShipToPartyEmpty) {
      navigate('/create-dp', {
        state: {
          isNewShipToParty: true,
          order_booking_id: location.state?.order_booking_id,
        }
      });
    }
  }, [shipToPartyCarts]);
  const handleEditShipToParty = (shipToPartyId, shipToPartyData) => {
    navigate('/dp-shiping-info', {
      state: {
        editMode: true,
        shipToPartyId: shipToPartyId,
        shipToPartyData: shipToPartyData,
      }
    })
  }
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent:'end', 
        p: { xs: 2, md: 0 },
        mr: { xs: 0, md: 4 }, 
        ml: {xs: 0, md: 2},
        position: {xs: 'sticky', md: 'relative'}, 
        top: 0, 
        zIndex: 1000, 
        boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
        background: {xs: '#FAFAFA', md: 'transparent'}
      }}>
        <CustomButton
          variant="filled"
          color="primary"
          label="Continue"
          loading={loading}
          onClick={handleConfirmAndProceed}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        />
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h2">Order Summary</Typography>
        <HumbeeTabs
          data={[...shipToPartyList.filter(item => !item.isAddTab), ...((shipToPartyList.filter(item => !item.isAddTab).length < 3)  ? [{ isAddTab: true, icon: 'Add' }] : [])]}
          currentTab={currentTab}
          onChange={(e, newValue) => {
            const tab = shipToPartyList[newValue];

            if (tab?.isAddTab) {
              handleAddShipToParty()
              return;
            }

            setCurrentTab(newValue);
            dispatch(selectShipToParty(tab.shipToPartyId));
          }}
          getTabIcon={(shipToParty, index, isSelected) => {
            if (shipToParty.isAddTab || shipToParty.icon) {
              return (
                <IconReusable 
                  name={shipToParty.icon} 
                  size={20} 
                />
              );
            }
            return null;
          }}
          getTabLabel={(shipToParty, index) => 
            shipToParty.isAddTab ? (
              'Add Ship to Party'
            ) : `Ship to Party ${index + 1}`
          }
          getTabKey={(shipToParty, index) => 
            shipToParty.isAddTab ? 'add-tab' : shipToParty.shipToPartyId
          }
          renderTabContent={(shipToParty) => {
            if (shipToParty.isAddTab) {
              return null
            }
            const { shipToPartyDetails, cart, totals } = shipToParty;
            const billingInfoAdapter = {
              company_name: shipToPartyDetails.company_name || '—',
              company_phone_no: shipToPartyDetails.company_phone_no || '—',
              selectedRole: shipToPartyDetails.selectedRole,
              company_address1: shipToPartyDetails.company_address1 || '',
              company_address2: shipToPartyDetails.company_address2 || '',
              company_address3: shipToPartyDetails.company_address3 || '',
              district_id: shipToPartyDetails.district_id || null, 
              location_id: shipToPartyDetails.location_id || null,
              state_id: shipToPartyDetails.state_id || null,
              company_pincode: shipToPartyDetails.company_pincode || '',
              erp_code: shipToPartyDetails.selected_esi_info.external_system_code
            };
            return (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ background: '#fff', p: 2, borderRadius: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <BillingInfo
                        buyer={billingInfoAdapter}
                        title="Ship to Party"
                        showRoleChip
                        showDeleteButton= {true}
                        onDelete={() => handleRemoveShipToParty(shipToParty.shipToPartyId)}
                        orderSummary
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 2,
                        background: '#fff',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant="labelD">Shipping Address</Typography>
                        <IconButton size="small" onClick={() => handleEditShipToParty(shipToParty.id, shipToPartyDetails)}>
                          <IconReusable name='Edit' color='#995A00' size={'24px'} />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Address
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.delivery_Address_line1}, {shipToPartyDetails.delivery_Address_line2}, {' '}
                          {shipToPartyDetails.delivery_Address_line3}, {shipToPartyDetails.delivery_district_name},  {shipToPartyDetails.delivery_location_name}, {shipToPartyDetails.delivery_state_name}, {shipToPartyDetails.delivery_company_pincode}
                        </Typography>
                      </Box>
                      <Typography variant="labelD" sx={{}}>
                        Logistic Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Delivery Type
                        </Typography> 
                        <Typography variant="p3">
                          {getDeliveryTypeLabel(shipToPartyDetails.delivery_type)}
                        </Typography> 
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Receiver’s Name
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.receiver_name}
                        </Typography> 
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1}}>
                        <Typography variant="labelM" sx={{ minWidth: '150px', color: '#666' }}>
                          Receiver’s Mobile number
                        </Typography> 
                        <Typography variant="p3">
                          {shipToPartyDetails.receiver_mobile}
                        </Typography> 
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="labelD">Products</Typography>
                  <CustomButton
                    variant="outlined"
                    size="small"
                    label="Add Products"
                    onClick={handleAddProducts}
                  />
                </Box>
                { !!updatedPricingGeo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconReusable name='InfoOutlined' size='20px' color='#CC0000' />
                      <Typography variant="p3" sx={{ color: '#CC0000'}} >
                        Prices have been changed as per the selected shipping address.
                      </Typography>
                  </Box>
                )}
               
                <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <Typography variant="labelD">
                    Total Quantity: 
                  </Typography>
                  <Typography variant="labelD">
                    {totals.total_quantity_in_metric_ton} {cart[0]?.unit}
                  </Typography>
                </Box>

                {renderCartItems(cart, shipToParty.shipToPartyId)}
              </Box>
            );
          }}
        />
      </Box>
      {console.log(pendingSTPEntry , 'pendingSTPEntry')}
      <HumbeeModal
        open={ShowSTPModal}
        onClose={() => {
          setShowSTPModal(false);
          setPendingSTPAction(null);
          setPendingSTPEntry(null);
        }}
        onConfirm={() => {
          if (pendingSTPAction === OperationType.ADD) {
            AddShiptoParty();
            return;
          }
          if (
            pendingSTPAction === OperationType.UPDATE &&
            pendingSTPEntry
          ) {
            updateShipToParty(pendingSTPEntry);
          }
        }}
        
        loading={loading}
        title={
          pendingSTPAction === OperationType.ADD
            ? 'New Ship to Party added'
            : 'Address updated for Ship to Party'
        }
        subtitle="Your order will be updated."
        confirmLabel="Update Order"
        cancelLabel="No"
        illustration={<DisclaimerSvg />}
        width={{ xs: 400, md: 400 }}
      />
    </>
  );
};

export default OrderSummary;
